import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import {
  ReportApi,
  TDeleteReportResult,
  TGetReportResult,
  TGetReportsResult,
  TSaveReportResult,
} from '../../services';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { createPagination, IPagination, IPaginationSnapshot } from '../pagination/Pagination';
import { createReport, IReport, Report, ReportFilter, ReportSaveDto } from '../report/Report';
import { createSimpleReport, ISimpleReport } from '../simple-report/SimpleReport';

function simpleToReport(simple: ISimpleReport): IReport | undefined {
  if (!simple) {
    return;
    // throw new Error('Cannot convert null report to Report');
  }
  return Report.create({
    reportSid: 0,
    reportNm: '',
    year: simple.year ?? '',
    month: simple.month ?? '',
    writeCmpny: '',
    writerNm: '',
    cnfirmNm: '',
    infraImage: {
      imageSid: 0,
      infraNm: '',
      imageNm: '',
      imageOrigNm: '',
      imagePath: '',
      imageSize: '',
      remark: null,
      useYn: 'Y',
      delYn: 'N',
      insertDt: 0,
      updateDt: 0,
    },
    appendix: null,
    conclusion: null,
    etc: null,
    accntInfos: simple.accntInfos ?? [],
    incidents: simple.incidents ?? [],
    loadBals: simple.loadBals ?? [],
    mngrInfos: simple.mngrInfos ?? [],
    monitorings: simple.monitorings ?? [],
    nasVols: simple.nasVols ?? [],
    nkss: simple.nkss ?? [],
    serviceHists: simple.serviceHists ?? [],
    vmhists: simple.vmhists ?? [],
    insertDt: 0,
    updateDt: 0,
  });
}
export const ReportStore = types
  .model('ReportStore')
  // --------------------------------------------------------------------------
  .props({
    pagination: createPagination(),
    reports: types.optional(types.array(Report), []),
    report: createReport(),
    simpleReport: createSimpleReport(),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setPagination: (pagination: IPaginationSnapshot) => {
      self.pagination = pagination as IPagination;
    },
    resetPagination: () => {
      self.pagination = { size: 20, page: 1 } as IPagination;
    },
    setReports: (reports: IReport[]) => {
      self.reports.replace(reports);
    },
    setReport: (report: IReport) => {
      self.report = report;
    },
    resetReport: () => {
      self.report = { reportSid: 0 } as IReport;
    },
  }))
  .actions((self) => ({
    setSimpleReport: (simpleReport: ISimpleReport) => {
      self.simpleReport.setProps(simpleReport);
      const convertedReport = simpleToReport(simpleReport);
      convertedReport && self.setReport(convertedReport);
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    gets: async (filter?: ReportFilter) => {
      const reportApi: ReportApi = new ReportApi(self.environment.api);
      const result: TGetReportsResult = await reportApi.gets(filter);
      if (result.kind === 'ok') {
        self.setReports(result.reports);
        result.pagination && self.setPagination(result.pagination);
      } else {
        console.error(result.kind);
      }
    },
    get: async (reportSid: number | 'generate') => {
      const reportApi: ReportApi = new ReportApi(self.environment.api);
      const result: TGetReportResult = await reportApi.get(reportSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        // if (result.responseInfo.resultCode === 'F') {
        //   alert(result.responseInfo.message);
        //   self.rootStore.responseStore.getResponseResult(result);
        //   self.resetReport();
        //   self.resetPagination();
        //   self.setSimpleReport(result.report as ISimpleReport);
        //   console.log('ReportStore.get: resultCode is F', result.responseInfo.message);
        // }
        if (!result.report) {
          console.warn('result.report is null');
          alert(result.responseInfo.message);
          // return;
        }
        // If reportSid is 'generate', set the simple report directly
        reportSid === 'generate'
          ? self.setSimpleReport(result.report)
          : self.setReport(result.report);
      } else {
        console.error(result.kind);
      }
    },
    checkDate: async (date: { year: string; month: string }) => {
      const reportApi: ReportApi = new ReportApi(self.environment.api);
      const result: TGetReportResult = await reportApi.checkDate(date);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        if (!result.report) {
          // console.warn('result.report is null');
          return;
        }
        self.setSimpleReport(result.report);
      } else {
        console.error(result.kind);
      }
    },
    downloadPdf: async (reportSid: number) => {
      const reportApi: ReportApi = new ReportApi(self.environment.api);
      const result = await reportApi.downloadPdf(reportSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        const blob = new Blob([result.pdf], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${reportSid}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        console.error(result.kind);
      }
    },
    save: async (report: ReportSaveDto) => {
      const reportApi: ReportApi = new ReportApi(self.environment.api);
      const result: TSaveReportResult = await reportApi.save(report);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setReports([
          ...self.reports.filter((item) => item.reportSid !== result.report.reportSid),
          result.report,
        ]);
        self.setReport(result.report);
      } else {
        console.error(result.kind);
      }
    },
    delete: async (reportSid: number) => {
      const reportApi: ReportApi = new ReportApi(self.environment.api);
      const result: TDeleteReportResult = await reportApi.delete(reportSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setReports(self.reports.filter((item) => item.reportSid !== reportSid));
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TReportStore = Instance<typeof ReportStore>;
type TReportStoreSnapshot = SnapshotOut<typeof ReportStore>;

export interface IReportStore extends TReportStore {}
export type TReportStoreKeys = keyof TReportStoreSnapshot & string;
export interface IReportStoreSnapshot extends TReportStoreSnapshot {}
export const createReportStore = () => types.optional(ReportStore, {} as TReportStore);
